import { CalendarIcon, CheckIcon, ExternalLinkIcon, SearchIcon } from "@chakra-ui/icons";
import { ChronoUnit, LocalDate } from "@js-joda/core";
import {
  ButtonGroup,
  Avatar,
  Button,
  Flex,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Row, createColumnHelper } from "@tanstack/react-table";
import React from "react";
import { Messages } from "../../../../core/api";
import CaregiverStatusSelect from "../../../../shared/components/CaregiverStatusSelect";
import DataTable from "../../../../shared/components/DataTable/DataTable";
import RangeDatePicker from "../../../../shared/components/DatePicker/RangeDatePicker";
import DebouncedInput from "../../../../shared/components/DebouncedInput";
import Link from "../../../../shared/components/Link";
import OfficesSelect from "../../../../shared/components/OfficeSelect";
import Page from "../../../../shared/components/Page";
import Select from "../../../../shared/components/Select";
import { getCaregiverOnline } from "../../../../shared/services/caregiverAppStatus";
import useEndpointDataTable, {
  EndpointPageRequest,
} from "../../../../shared/components/DataTable/useEndpointDataTable";
import { useCaregiversDashboardAiCapabilities } from "./caregiversDashboard.ai";
import useApi from "../../../../shared/hooks/useApi";
import useAuthData from "../../../../shared/hooks/useAuthInfo";
import { useEntityLink } from "../../../../shared/hooks/useEntityLink";
import AddIcon from "../../../../shared/icons/AddIcon";
import ExportNotesOutlineIcon from "../../../../shared/icons/ExportNotesOutlineIcon";
import { createFilters } from "../../../../shared/hooks/useFilters";
import { AgencyBranchId, OfficeId } from "../../../../shared/schema/schema";
import { fmap } from "../../../../shared/utils";
import { capitalize, toArrayOrNull } from "../../../../shared/utils/common";
import { dateFormatter } from "../../../../shared/utils/date-formatter";
import { getEntityStatusColorScheme } from "../../../../shared/utils/get-entity-status-color-scheme";
import { getFullName } from "../../../../shared/utils/get-full-name";
import { auth } from "../../../../core/auth";
import useGraphQLQuery from "../../../../shared/hooks/useGraphQLQuery";
import { GET_AGENCY_BRANCHES_FOR_FILTER_QUERY } from "../../caregiver.graphql";
import { formatCustomFieldValue } from "../../../custom-field/custom-field.utils";
import EntityNoteDrawerTrigger from "../../../note/components/EntityNoteDrawerTrigger";

const languageOptions = [
  { value: "English", label: "English" },
  { value: "Spanish", label: "Spanish" },
  { value: "Chinese", label: "Chinese" },
  { value: "Portuguese", label: "Portuguese" },
  { value: "French", label: "French" },
  { value: "Russian", label: "Russian" },
  { value: "Arabic", label: "Arabic" },
] as const;

const installationOptions = [
  { value: "installed", label: "Installed" },
  { value: "uninstalled", label: "Un-Installed" },
  { value: "notinstalled", label: "Not Installed" },
] as const;

type Caregiver = Messages["CaregiverListItem"];

type CaregiverFilters = {
  search: string | null;
  status: Messages["CaregiverStatus"][] | null;
  certifications: string[] | null;
  languages: string[] | null;
  officeId: OfficeId[] | null;
  agencyBranchId: AgencyBranchId[] | null;
  fromHireDate: LocalDate | null;
  toHireDate: LocalDate | null;
  installation: string | null;
  caregiverCode: string | null;
};

/**
 * The endpoint serves one page at a time, so an export walks the pages. It stops
 * at the same result window offset paging cannot see past, and says so rather
 * than handing over a file that is quietly missing rows.
 */
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_ROWS = 10_000;

/**
 * The photo, and what its border says, exactly as the caregivers page shows it:
 * grey when the app was never installed, green when it is installed and
 * connected, red when installed but not connected.
 *
 * Colours are the ones in `custom.css` (`.grey-circle` / `.green-circle` /
 * `.red-circle`) rather than theme tokens, so the two pages match.
 */
const PHOTO_SIZE = "50px";
const PHOTO_BORDER_WIDTH = "3px";

function photoBorderColor(caregiver: Caregiver): string {
  if (!caregiver.appInstalled) {
    return "grey";
  }

  const online = getCaregiverOnline(caregiver.id);

  // Unknown presence keeps the neutral border rather than claiming connected or
  // disconnected. See caregiverAppStatus.
  if (online === null) {
    return "grey";
  }

  return online ? "#46be8a" : "#ef5350";
}

function photoStatusLabel(caregiver: Caregiver): string {
  if (!caregiver.appInstalled) {
    return "App not installed";
  }

  const online = getCaregiverOnline(caregiver.id);

  if (online === null) {
    return "App installed";
  }

  return online ? "Online" : "Offline";
}

/**
 * `images/picture/face.png` is a legacy placeholder still stored on old rows. It
 * is a *relative* path, so handing it to an <img> resolves it against the current
 * route and 404s — the caregivers page has always swapped it, and null, for the
 * blank silhouette.
 */
const LEGACY_PHOTO_PLACEHOLDER = "images/picture/face.png";
const BLANK_PHOTO = "/admin/images/blank-profile.jpg";

function caregiverPhotoUrl(avatarUrl: string | null): string {
  if (avatarUrl === null || avatarUrl === LEGACY_PHOTO_PLACEHOLDER) {
    return BLANK_PHOTO;
  }

  return avatarUrl;
}

export default function CaregiversDashboardRoute() {
  const queryClient = useQueryClient();
  const { agencyMember } = useAuthData();
  const { api, queries } = useApi();
  const toast = useToast();

  const { createRangeDatePickerFilter } = createFilters<CaregiverFilters>();

  const customFieldsQuery = useQuery(queries.customField.list({ entity: "CAREGIVER" }));
  const certificationsQuery = useQuery(queries.caregiver.certifications());

  const branchesQuery = useGraphQLQuery({ document: GET_AGENCY_BRANCHES_FOR_FILTER_QUERY });

  const branchOptions = React.useMemo(
    () =>
      (branchesQuery.data?.agencyBranches.nodes ?? []).map((branch) => ({
        value: branch.id,
        label: branch.name,
      })),
    [branchesQuery.data?.agencyBranches.nodes]
  );

  const certificationOptions = React.useMemo(
    () =>
      (certificationsQuery.data ?? []).map((c) => ({
        value: c.certification,
        label: c.certification,
      })),
    [certificationsQuery.data]
  );

  const columns = React.useMemo(() => {
    return createCaregiversTableColumn({
      customFields: customFieldsQuery.data?.list.filter((x) => x.active && x.addToSettings) ?? [],
      onJoinRequestHandled: () =>
        queryClient.invalidateQueries({ queryKey: ["caregiver", "list"] }),
    });
  }, [customFieldsQuery.data?.list, queryClient]);

  const toQueryParams = React.useCallback(
    (request: Omit<EndpointPageRequest<CaregiverFilters>, "limit" | "offset">) => {
      const { filters } = request;

      // List params travel as comma-joined strings, which is the convention the
      // agency search endpoints already use.
      const list = (values: readonly (string | number)[] | null) =>
        values !== null && values.length > 0 ? values.join(",") : undefined;

      return {
        search: filters.search ?? undefined,
        status: list(filters.status),
        certifications: list(filters.certifications),
        languages: list(filters.languages),
        officeId: list(filters.officeId),
        agencyBranchId: list(filters.agencyBranchId),
        fromHireDate: filters.fromHireDate ?? undefined,
        toHireDate: filters.toHireDate ?? undefined,
        installation: filters.installation ?? undefined,
        caregiverCode: filters.caregiverCode ?? undefined,
        sortField: request.sortField ?? undefined,
        sortOrder: request.sortOrder,
      };
    },
    []
  );

  const { table, query, totalCount, filters, setFilter, metaButtonProps } = useEndpointDataTable({
    queryKey: ["caregiver", "list"],
    columns: columns,
    columnVisiblity: {
      storage: { key: "caregivers/dashboard", version: 1 },
      forceVisibleColumns: React.useMemo(() => ["id", "_photo"], []),
      initialSelected: [
        "firstName",
        "lastName",
        "displayId",
        "caregiverCode",
        "certifications",
        "status",
        "offices",
        "phoneNumbers",
        "address",
      ],
    },
    initialSorting: [{ id: "agencyAssocCreatedAt", desc: true }],
    filters: {
      initialState: {
        search: null,
        // The list opens on the caregivers an agency actually works with.
        status: ["ACTIVE"],
        certifications: null,
        languages: null,
        officeId: agencyMember.officeIds,
        agencyBranchId: null,
        fromHireDate: null,
        toHireDate: null,
        installation: null,
        caregiverCode: null,
      } satisfies CaregiverFilters as CaregiverFilters,
    },
    fetchPage: async (request) => {
      const response = await api.get("./caregivers/list", {
        query: {
          ...toQueryParams(request),
          limit: request.limit,
          offset: request.offset,
        },
      });

      return { items: response.items, totalCount: response.totalCount };
    },
    fetchAllForExport: async (request) => {
      const params = toQueryParams(request);
      const rows: Caregiver[] = [];

      while (rows.length < EXPORT_MAX_ROWS) {
        const response = await api.get("./caregivers/list", {
          query: { ...params, limit: EXPORT_PAGE_SIZE, offset: rows.length },
        });

        rows.push(...response.items);

        if (rows.length >= response.totalCount) {
          return rows;
        }

        if (rows.length >= EXPORT_MAX_ROWS) {
          toast({
            status: "warning",
            title: `Exported the first ${EXPORT_MAX_ROWS.toLocaleString()} of ${response.totalCount.toLocaleString()} caregivers`,
            description: "Narrow the filters to export the rest.",
          });
        }
      }

      return rows;
    },
  });

  // This page tells the omnibar what its own filters and columns are, while it is on screen.
  useCaregiversDashboardAiCapabilities({
    table,
    setFilter: (key, value) =>
      setFilter(key as Parameters<typeof setFilter>[0], value as Parameters<typeof setFilter>[1]),
  });

  const entityLink = useEntityLink();

  const handleClickRow = (event: React.MouseEvent<HTMLTableRowElement>, row: Row<Caregiver>) => {
    entityLink.open({ event, entity: { type: "Caregiver", id: row.original.id } });
  };

  const filterNode = (
    <>
      <InputGroup width="md">
        <DebouncedInput
          debounce={200}
          placeholder="Search by name, ID, code, address, phone..."
          value={filters.search ?? ""}
          onChange={(x) => setFilter("search", x === "" ? null : x)}
        />
        <InputRightElement>
          <SearchIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>

      <Flex align="center" gap={2} wrap="wrap">
        <CaregiverStatusSelect
          multiple={true}
          value={toArrayOrNull(filters.status)}
          onChange={(x) => setFilter("status", x ?? null)}
        />

        <OfficesSelect
          multiple={true}
          value={filters.officeId ?? null}
          onChange={(x) => setFilter("officeId", x ?? null)}
        />

        <Select
          label="Branches"
          multiple={true}
          options={branchOptions}
          value={toArrayOrNull(filters.agencyBranchId)}
          onChange={(x) => setFilter("agencyBranchId", x ?? null)}
        />

        <Select
          label="Certifications"
          multiple={true}
          options={certificationOptions}
          value={toArrayOrNull(filters.certifications)}
          onChange={(x) => setFilter("certifications", x ?? null)}
        />

        <Select
          label="Languages"
          multiple={true}
          options={languageOptions}
          value={toArrayOrNull(filters.languages)}
          onChange={(x) => setFilter("languages", x ?? null)}
        />

        <Select
          label="Installation"
          multiple={false}
          options={installationOptions}
          value={filters.installation}
          onChange={(x) => setFilter("installation", x ?? null)}
        />

        <InputGroup flexDirection="column" width="xs">
          <InputGroup width="xs">
            <RangeDatePicker
              {...createRangeDatePickerFilter({
                label: "Hire Date",
                // `placeholderText` is what reaches the input; without it the field
                // reads "Select date" and never says which date.
                placeholderText: "Hire Date",
                startDate: { name: "fromHireDate", value: filters.fromHireDate },
                endDate: { name: "toHireDate", value: filters.toHireDate },
                onChange: (name, value) => setFilter(name, value ?? null),
              })}
              id="hire-date"
              inputProps={{ width: "full" }}
            />
            <InputRightElement>
              <CalendarIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
            </InputRightElement>
          </InputGroup>
        </InputGroup>
      </Flex>
    </>
  );

  const actionNode = (
    <Menu isLazy>
      <MenuButton aria-label="Add caregiver" as={Button} colorScheme="blue" leftIcon={<AddIcon />}>
        Add Caregivers
      </MenuButton>
      <MenuList>
        <MenuItem as={Link} to="app.addCaregiver">
          Add a single caregiver
        </MenuItem>
        <MenuItem as={Link} to="app.professionals">
          Add multiple caregivers
        </MenuItem>
      </MenuList>
    </Menu>
  );

  // On the header line, opposite the title — where the caregivers page floats it,
  // and away from the add-caregiver dropdown.
  const boostNode = auth.isAllowedTo("caregivers_page_allow_boost") ? (
    <Button as={Link} colorScheme="blue" params={{ view: "sendDownload" }} to="app.communication">
      ✨ Boost Downloads ✨
    </Button>
  ) : null;

  return (
    <Page isLoading={query.isPending || query.isFetching}>
      <Page.Header>
        <Page.Title>Caregivers ({query.isPending ? "..." : totalCount})</Page.Title>
        {boostNode}
      </Page.Header>

      <Page.Content p={0}>
        <DataTable
          actionNode={actionNode}
          filterNode={filterNode}
          // Search on one line, the filters on the next. `alignItems` rather than
          // the `align` shorthand: the base sets `alignItems="center"`, which in a
          // column flex centres each row horizontally, and only the same prop name
          // reliably overrides it.
          filterNodeFlexProps={{ alignItems: "flex-start", direction: "column" }}
          isLoading={query.isPending}
          metaButtonProps={metaButtonProps}
          spacing="tight"
          table={table}
          onClickRow={handleClickRow}
        />
      </Page.Content>
    </Page>
  );
}

/**
 * Approving a join request opens the agency's existing status modal, which lives
 * on the Angular side; removing one deletes the request. Both go over the bridge
 * rather than being reimplemented here.
 */
function dispatchJoinRequestAction(
  action: "approve" | "remove",
  caregiverId: Messages["CaregiverListItem"]["id"],
  onDone: () => void
) {
  window.dispatchEvent(
    new CustomEvent("from-webapp-react", {
      detail: {
        type: "caregiverJoinRequest",
        payload: { action, caregiverId },
        onDone,
      },
    })
  );
}

function getAge(dateOfBirth: LocalDate): number {
  return ChronoUnit.YEARS.between(dateOfBirth, LocalDate.now());
}

function createCaregiversTableColumn(params: {
  customFields: Messages["CustomField"][];
  onJoinRequestHandled: () => void;
}) {
  const { accessor, display } = createColumnHelper<Caregiver>();

  // `meta.gqlSortKey` carries the endpoint's sort field name. DataTable also reads
  // its presence to decide whether a column offers a sort control, so a column
  // without one is simply not sortable — which is right for the ones the index
  // cannot order correctly.
  const columns = [
    display({
      // No `header`, deliberately. `useColumnVisibility` builds the "Columns"
      // list from every column that *has* one and defaults everything in that
      // list to hidden, so giving this an empty-string header put it in the list
      // (with a blank label) and hid the photo entirely. `_note-action` has no
      // header for the same reason.
      id: "_photo",
      cell: (props) => (
        <Tooltip label={photoStatusLabel(props.row.original)}>
          <Avatar
            borderColor={photoBorderColor(props.row.original)}
            borderWidth={PHOTO_BORDER_WIDTH}
            boxSize={PHOTO_SIZE}
            name={getFullName(props.row.original)}
            showBorder={true}
            src={caregiverPhotoUrl(props.row.original.avatarUrl)}
          />
        </Tooltip>
      ),
    }),
    accessor("lastName", {
      header: "Last Name",
      meta: { gqlSortKey: "lastName" },
    }),
    accessor("firstName", {
      header: "First Name",
      meta: { gqlSortKey: "firstName" },
    }),
    accessor("displayId", {
      header: "ID",
      // Sortable again. It was not, because per-agency fields were all indexed
      // as text and ordering the display id put 100 before 99. The index now
      // splits agencyData leaves by JSON type - see the dynamic templates in
      // OpenSearchFields - so displayId stays a number and orders numerically.
      // Needs the reindex that mapping change requires; Postgres was always
      // correct.
      meta: { gqlSortKey: "displayId" },
      cell: (props) => (
        <Button
          as="a"
          color="black"
          href={`${window.location.pathname}?caregiver=${props.row.original.id}`}
          rightIcon={<ExternalLinkIcon />}
          target="_blank"
          variant="link"
          onClick={(e) => e.stopPropagation()}
        >
          {props.getValue() ?? props.row.original.id}
        </Button>
      ),
    }),
    display({
      // Only join requests get these. The Pending Acceptance page that used to
      // own them is gone; the Angular side still owns both actions.
      id: "_join-request-action",
      cell: (props) =>
        props.row.original.status === "JOIN_REQUEST" ? (
          <ButtonGroup isAttached size="sm" variant="outline">
            <Button
              colorScheme="green"
              onClick={(e) => {
                e.stopPropagation();
                dispatchJoinRequestAction(
                  "approve",
                  props.row.original.id,
                  params.onJoinRequestHandled
                );
              }}
            >
              Approve
            </Button>
            <Button
              colorScheme="red"
              onClick={(e) => {
                e.stopPropagation();
                dispatchJoinRequestAction(
                  "remove",
                  props.row.original.id,
                  params.onJoinRequestHandled
                );
              }}
            >
              Remove
            </Button>
          </ButtonGroup>
        ) : null,
    }),
    display({
      id: "_note-action",
      cell: (props) => (
        <EntityNoteDrawerTrigger
          entity={{
            type: "Caregiver",
            id: props.row.original.id,
            name: getFullName(props.row.original),
          }}
        >
          <Button
            _hover={{ bg: "blue.100" }}
            colorScheme="blue"
            leftIcon={<ExportNotesOutlineIcon h={5} w={5} />}
            variant="ghost"
            onClick={(e) => e.stopPropagation()}
          >
            Notes
          </Button>
        </EntityNoteDrawerTrigger>
      ),
    }),
    accessor("caregiverCode", {
      header: "Caregiver Code",
      meta: { gqlSortKey: "caregiverCode" },
    }),
    accessor("certifications", {
      header: "Certifications",
      cell: (row) => row.getValue().join(", "),
    }),
    accessor("status", {
      header: "Status",
      meta: { gqlSortKey: "status" },
      cell: (row) => (
        <Tag colorScheme={getEntityStatusColorScheme(row.getValue())}>
          {capitalize(row.getValue())}
        </Tag>
      ),
    }),
    accessor("phoneNumbers", {
      id: "phoneNumbers",
      header: "Phone",
      // Already formatted by the endpoint, which is also what the search index
      // holds — reformatting here would be a second source of truth for it.
      meta: {
        csvFn: (x: Caregiver["phoneNumbers"]) => x.map((phone) => phone.formatted).join(", "),
      },
      cell: (row) =>
        row
          .getValue()
          .map((phone) => phone.formatted)
          .join(", "),
    }),
    accessor("address", {
      header: "Address",
      meta: { gqlSortKey: "address" },
    }),
    accessor("offices", {
      id: "offices",
      header: "Offices",
      meta: { csvFn: (x: Caregiver["offices"]) => x.map((o) => o.name).join(", ") },
      cell: (row) =>
        row
          .getValue()
          .map((o) => o.name)
          .join(", "),
    }),
    accessor("branches", {
      id: "branches",
      header: "Branches",
      meta: { csvFn: (x: Caregiver["branches"]) => x.map((b) => b.name).join(", ") },
      cell: (row) =>
        row
          .getValue()
          .map((b) => b.name)
          .join(", "),
    }),
    accessor("languages", {
      header: "Languages",
      cell: (row) => row.getValue().join(", "),
    }),
    accessor("hireDate", {
      header: "Hire Date",
      meta: { gqlSortKey: "hireDate" },
      cell: (row) => fmap(row.getValue(), dateFormatter.toDate),
    }),
    accessor("terminationDate", {
      header: "Termination Date",
      meta: { gqlSortKey: "terminationDate" },
      cell: (row) => fmap(row.getValue(), dateFormatter.toDate),
    }),
    accessor("dateOfBirth", {
      header: "Date of Birth",
      meta: { gqlSortKey: "dateOfBirth" },
      cell: (row) => fmap(row.getValue(), dateFormatter.toDate),
    }),
    accessor("dateOfBirth", {
      id: "age",
      header: "Age",
      meta: { csvFn: (value) => fmap(value, (dob) => String(getAge(dob))) },
      cell: (row) => fmap(row.getValue(), getAge),
    }),
    accessor("email", {
      header: "Email",
    }),
    accessor("gender", {
      header: "Gender",
      cell: (row) => fmap(row.getValue(), capitalize),
    }),
    accessor("appInstalled", {
      header: "App Installed",
      meta: { gqlSortKey: "appInstalled" },
      cell: (row) => (row.getValue() ? <CheckIcon color="green.500" /> : null),
    }),

    // Last seen was on the response all along and simply had no column. Not
    // sortable: it is the one field here the index does not carry, so ordering
    // by it would work in development and fail in production.
    accessor("lastSeen", {
      header: "Last Seen",
      cell: (row) =>
        row.row.original.isOnline
          ? "Online"
          : fmap(row.getValue(), (seen) => dateFormatter.toDateTime(seen)) ?? "",
    }),
    accessor("agencyAssocCreatedAt", {
      header: "Created at",
      meta: { gqlSortKey: "agencyAssocCreatedAt" },
      cell: (row) => dateFormatter.toDateOrDateTime(row.getValue()),
    }),
    accessor("createdBy", {
      header: "Created by",
      cell: (row) => fmap(row.getValue(), getFullName),
    }),
  ];

  for (const customField of params.customFields) {
    columns.push(
      display({
        id: `customField-${customField.id}`,
        header: customField.fieldName,
        meta: {
          csvFn: (_, x) => fmap(x.customFields[customField.fieldName], formatCustomFieldValue),
        },
        cell: ({ row }) => {
          return fmap(row.original.customFields[customField.fieldName], formatCustomFieldValue);
        },
      })
    );
  }

  return columns;
}
